import { Offer, OfferBlock } from '../../models/shared/Offer'
import BlockText from './BlockText';
import BlockHeading from './BlockHeading';
import BlockOrgCustomer from './BlockOrgCustomer';
import BlockSpacer from './BlockSpacer';
import BlockLineItems from './BlockLineItems';
import BlockMap from './BlockMap';
import BlockAttachments from './BlockAttachments';
import { useSortable } from '@dnd-kit/sortable';

interface Props {
    offer: Offer;
    ix: number;
    onOfferUpdate: (update: Partial<Offer>) => void;
    onMoveBlock?: (fromIndex: number, toIndex: number) => void;
    readOnly?: boolean;
    className?: string;
    autoFocus?: boolean;
    deal?: any; // Deal information for special blocks
}

export default function Block(props: Props) {
    const { offer, ix, onOfferUpdate, onMoveBlock } = props;
    const block = offer.blocks[ix];
    const blockType = block.type;

    // Setup sortable functionality
    const {
        attributes,
        listeners,
        setNodeRef,
        transition,
        isDragging,
        over
    } = useSortable({
        id: ix,
        data: {
            index: ix,
            block
        }
    });

    const style = {
        transition,
        opacity: isDragging ? 0.4 : 1,
        cursor: props.readOnly ? undefined : 'grab',
    };

    const handleBlockUpdate = (updatedBlock: OfferBlock) => {
        const updatedBlocks = [...offer.blocks];
        updatedBlocks[ix] = updatedBlock;
        onOfferUpdate({ blocks: updatedBlocks });
    }

    const handleBlockDelete = () => {
        const updatedBlocks = [...offer.blocks];
        updatedBlocks.splice(ix, 1);
        onOfferUpdate({ blocks: updatedBlocks });
    }

    const handleMoveUp = () => {
        if (ix > 0 && onMoveBlock) {
            onMoveBlock(ix, ix - 1);
        }
    };

    const handleMoveDown = () => {
        if (ix < offer.blocks.length - 1 && onMoveBlock) {
            onMoveBlock(ix, ix + 1);
        }
    };

    const renderBlock = () => {
        const isFirst = ix === 0;
        const isLast = ix === offer.blocks.length - 1;

        switch (blockType) {
            case 'text':
                return <BlockText
                    block={block}
                    readOnly={props.readOnly || !!over}
                    onBlockUpdate={handleBlockUpdate}
                    onBlockDelete={handleBlockDelete}
                    onMoveUp={!isFirst ? handleMoveUp : undefined}
                    onMoveDown={!isLast ? handleMoveDown : undefined}
                    autoFocus={props.autoFocus}
                />;
            case 'heading':
                return <BlockHeading
                    block={block}
                    readOnly={props.readOnly || !!over}
                    onBlockUpdate={handleBlockUpdate}
                    onBlockDelete={handleBlockDelete}
                    onMoveUp={!isFirst ? handleMoveUp : undefined}
                    onMoveDown={!isLast ? handleMoveDown : undefined}
                    autoFocus={props.autoFocus}
                />;
            case 'orgCustomer':
                return <BlockOrgCustomer
                    block={block}
                    deal={props.deal}
                    readOnly={props.readOnly || !!over}
                    onBlockUpdate={handleBlockUpdate}
                    onBlockDelete={handleBlockDelete}
                    onMoveUp={!isFirst ? handleMoveUp : undefined}
                    onMoveDown={!isLast ? handleMoveDown : undefined}
                />;
            case 'spacer':
                return <BlockSpacer
                    block={block}
                    readOnly={props.readOnly || !!over}
                    onBlockUpdate={handleBlockUpdate}
                    onBlockDelete={handleBlockDelete}
                    onMoveUp={!isFirst ? handleMoveUp : undefined}
                    onMoveDown={!isLast ? handleMoveDown : undefined}
                />;
            case 'lineItems':
                return <BlockLineItems
                    block={block}
                    offer={props.offer}
                    readOnly={props.readOnly || !!over}
                    onBlockUpdate={handleBlockUpdate}
                    onOfferUpdate={props.onOfferUpdate}
                    onBlockDelete={handleBlockDelete}
                    onMoveUp={!isFirst ? handleMoveUp : undefined}
                    onMoveDown={!isLast ? handleMoveDown : undefined}
                />;
            case 'map':
                return <BlockMap
                    ix={ix}
                    block={block}
                    deal={props.deal}
                    readOnly={props.readOnly || !!over}
                    onBlockUpdate={handleBlockUpdate}
                    onBlockDelete={handleBlockDelete}
                    onMoveUp={!isFirst ? handleMoveUp : undefined}
                    onMoveDown={!isLast ? handleMoveDown : undefined}
                />;
            case 'attachments':
                return <BlockAttachments
                    block={block}
                    deal={props.deal}
                    readOnly={props.readOnly || !!over}
                    onBlockUpdate={handleBlockUpdate}
                    onBlockDelete={handleBlockDelete}
                    onMoveUp={!isFirst ? handleMoveUp : undefined}
                    onMoveDown={!isLast ? handleMoveDown : undefined}
                />;
            default:
                return null;
        }
    }

    if (props.readOnly) {
        return <div className={`d-flex ${props.className}`}>
            {renderBlock()}
        </div>
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`block-draggable d-flex ${props.className}`}
            {...attributes}
            {...listeners}
        >
            {renderBlock()}
        </div>
    );
}
